import React, { useContext, useState } from 'react'
import { Button } from '@/components/ui/button'

import { ReactDatePicker as DatePicker } from '../../../components/DatePicker/DatePicker'

import 'react-datepicker/dist/react-datepicker.css'

import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridItemChangeEvent,
} from '@progress/kendo-react-grid'
import { Upload, UploadOnAddEvent } from '@progress/kendo-react-upload'
import { CreatePromoMaterial } from 'api/ClientArea/CreatePromoMaterials'
import { PreviewPromoMaterial } from 'api/ClientArea/PreviewPromoMaterials'
import MoreButton from 'components/MoreButton/MoreButton'
import { Option, Select } from 'components/Select'
import { toBase64 } from 'utils/file.helpers'

import { NameDto } from '../../../api/Common/Types'
import { LoadingOverlay } from '../../../components/LoadingOverlay/LoadingOverlay'
import { errorToast, ToastContext } from '../../../components/Toast/Toast'
import { AuthSessionContext } from '../../../utils/AuthContext'
import { convertDateToUTC, getOneYearFromCurrentDate } from '../../../utils/date'
import { UploadTooltip } from './components/UploadTooltip'
import { FileInfo } from './RelatedPromoMaterialTable'

import styles from './RelatedPromoMaterialUploadTable.module.scss'

interface FileInfoUpload extends Omit<FileInfo, 'getRawFile'> {
  getRawFile(): File
}
interface OuterProps {
  caLanguages: NameDto<string>[]
  promoMaterialsPreview: PreviewPromoMaterial[]
  sizes: NameDto[]
  onSubmit(data: FileInfo[]): Promise<void>
  onPromoMaterialValidate(
    values: Omit<CreatePromoMaterial, 'id'>[]
  ): Promise<PreviewPromoMaterial[] | undefined>
}

export const RelatedPromoMaterialUploadTable: React.FC<OuterProps> = (props) => {
  const { caLanguages, onSubmit, onPromoMaterialValidate, sizes } = props
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const [isLoading, setLoading] = useState<boolean>(false)
  const [isPreviewValidated, setPreviewValidated] = useState<boolean>(false)
  const [promoAssetFiles, setPromoAssetFiles] = useState<FileInfo[]>([])

  const handleUpload = async () => {
    if (promoAssetFiles?.length >= 1) {
      try {
        setLoading(true)
        await onSubmit(promoAssetFiles)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleAdd = async (event: UploadOnAddEvent): Promise<void> => {
    const files = [
      ...promoAssetFiles,
      ...event.newState.map((newStateItem) => {
        return { ...newStateItem, endDateTime: getOneYearFromCurrentDate() }
      }),
    ]

    const edit = await Promise.all(
      files
        .filter((x): x is FileInfoUpload => !!x?.getRawFile)
        .map(async (x) => {
          const base64 = await toBase64(x.getRawFile())
          const promoAsset = typeof base64 === 'string' ? base64 : ''
          return {
            ...x,
            id: null,
            inEdit: true,
            enabled: true,
            promoAsset,
          }
        })
    )

    setPromoAssetFiles(edit)
    setPreviewValidated(false)
  }

  const handleUploadValidation = async () => {
    if (promoAssetFiles?.length >= 1) {
      try {
        setLoading(true)

        const newPromoAssetFiles: Omit<CreatePromoMaterial, 'id'>[] = promoAssetFiles.map((x) => ({
          name: x.name,
          promoAsset: x.promoAsset,
          languageId: x.languageId,
          endDateTime: x.endDateTime,
          enabled: x.enabled,
        }))

        const promoAssetsPreview = await onPromoMaterialValidate(newPromoAssetFiles)

        if (promoAssetsPreview) {
          const previewPromoAssets = promoAssetFiles
            .filter((_, index) => !!promoAssetsPreview[index])
            .map((x, index) => {
              const promoAssetPreview = promoAssetsPreview[index]
              return {
                ...x,
                enabled: promoAssetPreview.enabled,
                endDateTime: promoAssetPreview.endDateTime,
                contentType: promoAssetPreview.contentType,
                fileSize: promoAssetPreview.size,
              }
            })

          if (newPromoAssetFiles.length === previewPromoAssets.length) {
            setPromoAssetFiles(previewPromoAssets)
            setPreviewValidated(true)
          }
        }
      } catch (e) {
        console.error(e)
        setPreviewValidated(false)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleChange = (event: GridItemChangeEvent): void => {
    if (event.field) {
      event.dataItem[event.field] = event.value
      setPromoAssetFiles([...promoAssetFiles])
      setPreviewValidated(false)
    }
  }

  const handleRemove = (uid: string): void => {
    const files = promoAssetFiles.filter((x) => x.uid !== uid)
    setPromoAssetFiles(files)
    setPreviewValidated(false)
  }

  const handleGridChange =
    (gridCellProps: GridCellProps) => (value: React.SyntheticEvent<HTMLInputElement> | boolean) => {
      if (gridCellProps.onChange) {
        gridCellProps.onChange({
          ...gridCellProps,
          value,
          syntheticEvent: typeof value === 'boolean' ? ({} as React.SyntheticEvent) : value,
        })
        setPreviewValidated(false)
      }
    }

  const handleDateChange = (gridCellProps: GridCellProps) => (date: Date | null) => {
    const field = gridCellProps.field as string

    const value = date ? convertDateToUTC(date).toISOString() : null

    gridCellProps.dataItem[field] = value

    setPromoAssetFiles([...promoAssetFiles])
    setPreviewValidated(false)
  }

  const isValid = (): boolean => {
    return (
      promoAssetFiles?.length > 0 &&
      promoAssetFiles?.every(
        (x: FileInfo) => x?.languageId && x?.promoAsset && x?.name?.length >= 3
      )
    )
  }

  return (
    <DialogContent className='max-w-fit w-auto'>
      <DialogHeader>
        <DialogTitle>Bulk Promo Material Upload</DialogTitle>
      </DialogHeader>

      <UploadTooltip sizes={sizes}>
        <Upload batch={false} autoUpload={false} multiple showFileList onAdd={handleAdd} />
      </UploadTooltip>
      <Grid data={promoAssetFiles} editField='inEdit' onItemChange={handleChange} resizable>
        <Column field='name' title='Name' width={250} />
        <Column
          field='languageId'
          title='Language'
          cell={(gridCellProps) => (
            <td>
              <Select
                value={
                  gridCellProps?.field
                    ? gridCellProps.dataItem[gridCellProps.field]?.toString()
                    : ''
                }
                onChange={handleGridChange(gridCellProps)}
                fullWidth
                placeholder='English or CA Language should be chosen'
              >
                {(caLanguages || []).map((x) => (
                  <Option value={x.id.toString()} key={x.id}>
                    {x.name}
                  </Option>
                ))}
              </Select>
            </td>
          )}
          width={150}
        />
        <Column
          field='endDateTime'
          title='End Date'
          width={180}
          cell={(gridCellProps: GridCellProps) => {
            return (
              <td>
                <DatePicker
                  portalId='root-portal'
                  wrapperClassName={styles.datePicker}
                  popperClassName={styles.datePickerPopper}
                  showYearDropdown
                  dropdownMode='select'
                  isClearable
                  dateFormat={auth?.user?.dateFormatType?.name}
                  selected={gridCellProps.dataItem[gridCellProps.field || '']}
                  disabled={false}
                  onChange={(date) => handleDateChange(gridCellProps)(date)}
                  customInput={<input type='text' autoComplete='off' />}
                  onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.key !== 'Backspace') {
                      event.preventDefault()
                    }
                  }}
                />
              </td>
            )
          }}
        />
        <Column
          field='enabled'
          title='Active'
          width='45px'
          cell={(gridCellProps) => {
            return (
              <td style={{ textAlign: 'center' }}>
                <input
                  type='checkbox'
                  checked={gridCellProps.dataItem.enabled}
                  onChange={(e) => handleGridChange(gridCellProps)(e.target.checked)}
                />
              </td>
            )
          }}
        />
        <Column
          field='Action'
          width={80}
          cell={(gridProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Delete',
                    onClick: () => handleRemove(gridProps.dataItem.uid),
                  },
                  {
                    name: 'Apply to all',
                    onClick: () => {
                      const selectedLanguageId = gridProps.dataItem.languageId
                      const selectedEndDate = gridProps.dataItem.endDateTime
                      const isActive = gridProps.dataItem.enabled

                      if (!selectedLanguageId) {
                        setToast(errorToast('Language is not selected.'))
                        return
                      }

                      const updatedFiles = promoAssetFiles.map((file) => ({
                        ...file,
                        languageId: selectedLanguageId,
                        endDateTime: selectedEndDate,
                        enabled: isActive,
                      }))
                      setPromoAssetFiles(updatedFiles)
                    },
                  },
                ]}
              />
            </td>
          )}
        />
      </Grid>
      <DialogFooter>
        {isPreviewValidated ? (
          <Button disabled={!isValid() || isLoading} onClick={handleUpload}>
            Upload
          </Button>
        ) : (
          <Button disabled={!isValid()} onClick={handleUploadValidation}>
            Validate
          </Button>
        )}
      </DialogFooter>
      <LoadingOverlay isLoading={isLoading} />
    </DialogContent>
  )
}
